import errorIcon from '@p2p/assets/img/error_icon.png';
import {
  CopyIcon,
  CopyJetIcon,
  CopySolIcon,
  CopyStardaIcon,
  CopyVolnaIcon,
  LoadingIcon,
} from '@p2p/assets/svg';
import { ModalIncrementAppliedV1 } from '@p2p/components';
import { TGetOrderResponse } from '@p2p/services';
import { PaymentType } from '@p2p/shared/constants';
import { formatNumberWithSpaces, formatPhoneNumber } from '@p2p/shared/functions';
import { useCopyFeedback } from '@p2p/shared/hooks';
import classNames from 'classnames';
import { useMemo } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { TransferCard, TransferHead, TransferLogo } from '..';
import styles from './TransferForm.module.scss';

const TIME_SHOW_COPY_TEXT = 2 * 1000;

type TransferFormProps = {
  order: TGetOrderResponse;
  id: string;
  openModal: () => void;
  isPlayed: boolean;
};

const SBER_BANK_LABEL = '_sber';
const TINKOFF_BANK_LABEL = '_tinkoff';

export const TransferForm = ({ order, id, openModal, isPlayed }: TransferFormProps) => {
  const { isCopied: isCopyNumber, onCopy: onCopyNumber } = useCopyFeedback(TIME_SHOW_COPY_TEXT);
  const { isCopied: isCopySum, onCopy: onCopySum } = useCopyFeedback(TIME_SHOW_COPY_TEXT);

  const copyPhone = useMemo(() => {
    const { payment_type, phone_number, account_number } = order.requisite;
    return payment_type === PaymentType.PAYMENT_TYPE_C2C
      ? account_number?.toString() || ''
      : phone_number?.toString() || '';
  }, [order?.requisite]);

  const phone = useMemo(() => {
    const { payment_type, phone_number, account_number } = order.requisite;
    return payment_type === PaymentType.PAYMENT_TYPE_C2C
      ? account_number?.toString() || ''
      : formatPhoneNumber(phone_number?.toString() || '');
  }, [order?.requisite]);

  const themeName: string = order?.payment_form?.split('_')?.[0];
  const copyImage = useMemo(() => {
    switch (themeName) {
      case 'sol':
        return <CopySolIcon />;
      case 'jet':
        return <CopyJetIcon />;
      case 'volna':
        return <CopyVolnaIcon />;
      case 'starda':
        return <CopyStardaIcon />;
      case 'drip':
        return <CopySolIcon />;
      case 'izzi':
        return <CopySolIcon />;
      case 'legzo':
        return <CopySolIcon />;
      case 'fresh':
        return <CopySolIcon />;
      case 'monro':
        return <CopySolIcon />;
      default:
        return <CopyIcon />;
    }
  }, [themeName]);

  const isShowGuideTinkoff = useMemo(
    () => order?.payment_form?.slice(-TINKOFF_BANK_LABEL.length) === TINKOFF_BANK_LABEL,
    [order?.payment_form],
  );

  const isShowGuideSber = useMemo(
    () =>
      order?.payment_form?.slice(-5) === SBER_BANK_LABEL &&
      order?.requisite.payment_type === PaymentType.PAYMENT_TYPE_SBP,
    [order?.payment_form, order?.requisite.payment_type],
  );

  const paymentType = order?.requisite.payment_type;

  return (
    <main className={classNames(styles.payment_page, styles[themeName])}>
      {order?.id && order?.increment_applied && (
        <ModalIncrementAppliedV1
          uid={order?.id}
          sumText={`${order?.expected_income.toLocaleString('ru-RU')} ${order?.currency_code_to}`}
        />
      )}
      <TransferLogo />
      <div className={styles.payment_dialog}>
        <TransferHead id={id} status={themeName} />
        <div className={styles.payment_dialog__body}>
          <TransferCard
            isShowStatus
            time={order?.time_left}
            price={order?.expected_income}
            code={order?.currency_code_to}
            status={themeName}
          />
          {paymentType === PaymentType.PAYMENT_TYPE_SBP && (
            <p className={styles.warningRed}>
              <img src={errorIcon} alt="error icon" />
              <p>
                Важно! Перевод необходимо выполнить строго на банк получателя средств, иначе
                денежные средства будут утеряны
              </p>
            </p>
          )}
          <div className={styles.payment_dialog__platform}>
            <div className={styles.payment_dialog__platform_wr}>
              <div className={styles.payment_dialog__platform_image_wr}>
                <img
                  src={order.bank_svg_image}
                  alt=""
                  className={styles.payment_dialog__platform_image}
                />
              </div>
              <div className={styles.payment_dialog__platform_content}>
                <div className={styles.payment_dialog__platform_title}>
                  {paymentType === PaymentType.PAYMENT_TYPE_SBP && 'Оплата через СБП'}
                  {paymentType === PaymentType.PAYMENT_TYPE_C2C && 'Перевод на карту'}
                </div>
                <div className={styles.payment_dialog__platform_text}>
                  Переведите {formatNumberWithSpaces(order?.expected_income)} RUB в{' '}
                  {order?.requisite?.bank?.name}. <br />{' '}
                  {paymentType !== PaymentType.PAYMENT_TYPE_TRANSFER &&
                    'Не оставляйте комментарий к переводу'}
                </div>
              </div>
            </div>
            <div className={styles.payment_dialog__platform_text}>
              Переведите {formatNumberWithSpaces(order?.expected_income)} RUB в{' '}
              {order?.requisite?.bank?.name}. <br />{' '}
              {paymentType !== PaymentType.PAYMENT_TYPE_TRANSFER &&
                'Не оставляйте комментарий к переводу'}
            </div>
          </div>
          {isShowGuideTinkoff && (
            <div>
              <label className={styles.input_field}>
                <div className={styles.input_field_label}>Инструкция</div>
                <div className={styles.payment_dialog__platform_text}>
                  <ul style={{ paddingLeft: '16px' }}>
                    <li>Перейдите в приложение Т-банка (Tinkoff)</li>
                    <li>
                      Далее выберите раздел &#34;Платежи&#34; и &#34;Перевод по номеру телефона&#34;
                    </li>
                    <li>Укажите страну, в которую необходимо сделать перевод</li>
                    <li>Выберите банк {order?.requisite?.bank?.name} и отправьте точную сумму.</li>
                  </ul>
                </div>
              </label>
            </div>
          )}
          {isShowGuideSber && (
            <label className={styles.input_field}>
              <div className={styles.input_field_label}>Инструкция</div>
              <div className={styles.payment_dialog__platform_text}>
                <ul style={{ paddingLeft: '16px' }}>
                  <li>Перейдите в приложение СберБанк</li>
                  <li>Зайдите в меню &#34;Платежи&#34; - &#34;В другую страну&#34;</li>
                  <li>Выберите страну, в которую нужно сделать перевод</li>
                  <li>Вставьте номер телефона получателя</li>
                  <li>
                    Выберите банк <b>{order?.requisite?.bank?.name}</b>
                  </li>
                  <li>Отправьте точную сумму</li>
                </ul>
              </div>
            </label>
          )}
          <form action="#" className={styles.payment_dialog__form}>
            <label className={styles.input_field}>
              <div className={styles.input_field_label}>
                {paymentType === PaymentType.PAYMENT_TYPE_SBP && 'Номер телефона'}
                {paymentType === PaymentType.PAYMENT_TYPE_C2C && 'Номер карты'}
              </div>
              <div className={styles.input_field__wr}>
                <input type="text" className={styles.input_field__input} value={phone} disabled />
                <CopyToClipboard text={copyPhone} onCopy={onCopyNumber}>
                  <div className={styles.input_field__copy}>
                    {isCopyNumber ? 'Скопировано' : 'Копировать'} {copyImage}
                  </div>
                </CopyToClipboard>
              </div>
            </label>
            <label className={styles.input_field}>
              <div className={styles.input_field_label}>Сумма перевода</div>
              <div className={styles.input_field__wr}>
                <input
                  type="text"
                  className={styles.input_field__input}
                  value={`${formatNumberWithSpaces(order?.expected_income)} ${
                    order?.currency_code_to
                  }`}
                  disabled
                />
                <CopyToClipboard text={order?.expected_income.toString() || ''} onCopy={onCopySum}>
                  <div className={styles.input_field__copy}>
                    {isCopySum ? 'Скопировано' : 'Копировать'} {copyImage}
                  </div>
                </CopyToClipboard>
              </div>
            </label>
            <div className={styles.payment_dialog__form_box}>
              <label className={styles.input_field}>
                <div className={styles.input_field_label}>Получатель</div>
                <div className={styles.input_field__input}>
                  <div className={styles.ellipsis}>{order.requisite.fio}</div>
                </div>
              </label>
              <label className={styles.input_field}>
                <div className={styles.input_field_label}>Банк получателя</div>
                <div className={styles.input_field__input}>
                  <div className={styles.ellipsis}>
                    {order?.requisite.bank.name || 'Банк не найден'}
                  </div>
                </div>
              </label>
            </div>
          </form>
          <div className={styles.attention_block}>
            <div className={styles.attention_block__wr}>
              <div className={styles.attention_block__content_wr}>
                <p className={styles.attention_block__text}>
                  Переводите точную сумму одним переводом! <br /> Реквизиты для оплаты меняются
                  каждый платеж!
                  {/* Если возник вопрос, задайте его в нашем чате. */}
                </p>
                {/* <a href="#" className={styles.attention_block__link}> Чат техподдержки </a> */}
              </div>
            </div>
          </div>
          <div>
            {isPlayed ? (
              <button type="button" className={classNames(styles.playedDisabled, styles.played)}>
                Поиск платежа{' '}
                <span className={styles.loader}>
                  <LoadingIcon />
                </span>
              </button>
            ) : (
              <button type="button" className={styles.played} onClick={openModal}>
                Я оплатил
              </button>
            )}
            <a
              href={order?.merchant_failed_page}
              className={classNames(styles.btn, styles.btn_secondary)}
            >
              Отменить платеж
            </a>
          </div>
        </div>
      </div>
    </main>
  );
};
